import className from 'classnames'
import React, { useCallback } from 'react'
import { Icon } from '@lightspeed/design-system-react'
import { vn } from 'vend-number'
import { reactular } from '@vendhq/reactular'
import { useT } from '@transifex/react'
import { LineItem } from '../../../models/line-item'
import { dateFormat } from '../../../filters/wr-date-format'
import { displayRound } from '../../../filters/display-round'
import {
  useRegisterManager,
  useSaleInventoryData,
  useVdFeatureManager,
} from '../../../ng-compat/hooks'
import {
  getVariantsDisplayNames,
  hasVariants,
  isDeletedProduct,
  isPriceDiscounted,
  isGiftCard,
} from '../../../functions/lineItem'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  openFulfilmentLineItemPopover,
  resetExpandedLineItemId,
  selectExpandedLineItemId,
  selectOpenFulfilmentPopoverLineItemId,
  setExpandedLineItemId,
} from '../../../state/slices/uiStateSlice'
import {
  FulfilmentDetail,
  isDefinedFulfilmentType,
} from '../../../models/fulfilment'
import { EVENT } from '../../../constants'
import {
  selectFulfilmentNoteIsInvalid,
  selectIsSaleUnfulfilled,
  selectSaleAttributesHasFulfilment,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { selectSaleReturnFor } from '../../../state/selectors'
import { LineItemTotalPrice } from './LineItemTotalPrice'
import { LineItemTotalBasePrice } from './LineItemTotalBasePrice'
import { LineItemCustomFields } from './LineItemCustomFields'
import { LineItemFulfilmentPopover } from './fulfilment/popover/LineItemFulfilmentPopover'
import { useSaleFulfilment } from './fulfilment/useSaleFulfilment'
import { CurrentView } from './fulfilment/popover/LineItemFulfillmentContext'
import { LineItemLockedIcon } from './LineItemLockedIcon'

interface Props {
  hasInvalidInput: boolean
  lineItem: LineItem
  editAllowedForConfirmed?: boolean
  onRemove: () => void
  onToggle: (event: React.MouseEvent) => void
}

const _context = 'Sell screen line item'

export const LineItemDisplay: React.FC<Props> = ({
  hasInvalidInput,
  lineItem,
  editAllowedForConfirmed = false,
  onRemove,
  onToggle,
}) => {
  const t = useT()
  const dispatch = useAppDispatch()
  const registerManager = useRegisterManager()
  const outlet = registerManager.getActiveOutlet()
  const outletTimeZone = outlet && outlet.time_zone
  const isMarkedAsUnfulfilled = useAppSelector(selectIsSaleUnfulfilled)
  const isReferencedReturn = !!useAppSelector(selectSaleReturnFor)

  const isSalesPersonFFEnabled = useVdFeatureManager().get(
    'sales_person_attribution'
  )

  const reactSaleSummary = useVdFeatureManager().get('react_sale_summary')
  const isFulfillmentLifFFEnabled = useVdFeatureManager().get('fulfillment_lif')
  const isFulfillmentLifB3FFEnabled = useVdFeatureManager().get(
    'fulfillment_lif_b3'
  )

  const readonly = isGiftCard(lineItem.product)
  const expandedLineItemId = useAppSelector(selectExpandedLineItemId)
  const isExpanded = expandedLineItemId === lineItem.localId

  const fulfilmentPopoverLineItemId = useAppSelector(
    selectOpenFulfilmentPopoverLineItemId
  )
  const fulfilmentPopoverOpen = fulfilmentPopoverLineItemId === lineItem.localId

  const { setFulfilmentType, fulfilmentDetail } = useSaleFulfilment(
    lineItem.localId
  )

  const saleInventoryData = useSaleInventoryData()

  const fulfilmentDetailsRequired =
    isFulfillmentLifB3FFEnabled &&
    !isDefinedFulfilmentType(lineItem.fulfilmentType) &&
    saleInventoryData.shouldValidateInventory(lineItem) &&
    !saleInventoryData.hasInventory(lineItem)

  const isFulfilmentNoteInvalid = useAppSelector(selectFulfilmentNoteIsInvalid)
  const hasFulfilmentAttributes = useAppSelector(
    selectSaleAttributesHasFulfilment
  )

  const onToggleLocal = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation()

    if (fulfilmentPopoverOpen) {
      return
    }

    if (isFulfillmentLifFFEnabled) {
      dispatch(
        isExpanded
          ? resetExpandedLineItemId()
          : setExpandedLineItemId(lineItem.localId)
      )
    }
    onToggle(e)
  }

  const onFulfilmentDetailsSet = useCallback(
    (fulfilmentDetails: FulfilmentDetail) => {
      setFulfilmentType(fulfilmentDetails.type, fulfilmentDetails.note)
      saleInventoryData.disableInventoryValidation(lineItem)
      saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)
    },
    [setFulfilmentType, saleInventoryData, lineItem]
  )

  const onClosePopover = () => dispatch(openFulfilmentLineItemPopover(''))

  const productInventoryLevel =
    saleInventoryData.getInventoryData(lineItem)?.inventoryLevel ?? 0

  const showErrorIcon =
    hasInvalidInput ||
    fulfilmentDetailsRequired ||
    (isDefinedFulfilmentType(lineItem.fulfilmentType) &&
      isFulfilmentNoteInvalid &&
      !hasFulfilmentAttributes)

  const locked = !editAllowedForConfirmed && lineItem.confirmed

  return (
    <ul>
      <li
        // a proper fix would involve setting this tabIndex and `on*` callbacks to an
        // interactive element wrapping all the elements inside this `li` except
        // for the element tied to the delete action.
        // This involves styling changes and some logic is built on those styles,
        // so at this point we might as well leave this as is knowing this will get
        // rebuild on React and this mistake can be avoided when we do that.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className={className('li-cell-summary-container', {
          'vd-pb4': reactSaleSummary,
          'li-cell-summary-container--readonly': readonly,
        })}
        onClick={onToggleLocal}
        id={`wr-sale-line-item-${lineItem.id || lineItem.localId}`}
      >
        <span
          tabIndex={0}
          role="button"
          aria-pressed="false"
          className="vd-pl1 vd-pr1 vd-pb4"
        >
          <Icon
            className={className('vd-text--secondary wr-expandable-chevron')}
            icon={
              isExpanded && isFulfillmentLifFFEnabled
                ? 'angle-down'
                : 'angle-right'
            }
          />
        </span>

        <div className="li-cell-summary li-cell-summary--quantity">
          {!saleInventoryData.isLoading && !lineItem.calculating && (
            <LineItemFulfilmentPopover
              open={
                isFulfillmentLifB3FFEnabled &&
                fulfilmentPopoverOpen &&
                !isReferencedReturn
              }
              productName={lineItem.product.name}
              productQuantity={productInventoryLevel}
              onFulfilmentDetailsSet={onFulfilmentDetailsSet}
              onClose={onClosePopover}
              defaultView={
                saleInventoryData.shouldValidateInventory(lineItem) &&
                !saleInventoryData.hasInventory(lineItem)
                  ? CurrentView.DECISION
                  : undefined
              }
              defaultFulfilmentDetail={fulfilmentDetail}
            >
              {showErrorIcon ? (
                <span data-testid="line-item-icon-error">
                  <Icon
                    icon="exclamation-triangle"
                    className="wr-input-error-icon"
                    title={t('There is a problem with this item', {
                      _context,
                    })}
                  />
                </span>
              ) : (
                <QuantitySummaryText
                  quantity={lineItem.quantity}
                  prefix={
                    isFulfillmentLifFFEnabled
                      ? ''
                      : isMarkedAsUnfulfilled
                      ? '0/'
                      : ''
                  }
                />
              )}
            </LineItemFulfilmentPopover>
          )}
        </div>

        <div
          className={className(
            'li-cell-summary li-cell-summary--name-container',
            { 'vd-pb4': !reactSaleSummary }
          )}
        >
          <div
            className="li-cell-summary pro-li-cell-summary-name vd-text-label"
            title={lineItem.product.name}
          >
            {lineItem.product.name}

            {hasVariants(lineItem) && (
              <div className="vd-text-supplementary">
                {getVariantsDisplayNames(lineItem)}
              </div>
            )}

            {isSalesPersonFFEnabled && lineItem.salesperson && (
              <div className="vd-text-supplementary">
                {t('Sold by: {salesPerson}', {
                  _context,
                  salesPerson: lineItem.salesperson.displayName,
                })}
              </div>
            )}

            {isDeletedProduct(lineItem) && (
              <div className="vd-text-supplementary">
                Deleted on{' '}
                {dateFormat(
                  lineItem.product.deleted_at,
                  'DD MMMM YYYY',
                  outletTimeZone
                )}
              </div>
            )}

            {lineItem.giftCardNumber && (
              <div className="vd-text-supplementary">
                {t('Card Number: {giftCardNumber}', {
                  _context,
                  giftCardNumber: lineItem.giftCardNumber,
                })}
              </div>
            )}

            {lineItem.promotionDetails &&
              lineItem.promotionDetails.map(promotionDetail => (
                <div
                  key={promotionDetail.id!}
                  className="vd-text-supplementary"
                >
                  <Icon
                    icon="ticket"
                    className="li-cell-actions-promotion-icon"
                  />
                  <span className="pro-promotion-detail-name">
                    {promotionDetail.name}
                  </span>
                </div>
              ))}
          </div>

          <LineItemCustomFields lineItem={lineItem} />
        </div>

        <div className="li-cell-summary--totals-container">
          {lineItem.calculating ? (
            <span className="vd-loader vd-loader--small" />
          ) : (
            <div>
              <div className="li-cell-summary--total-price">
                <LineItemTotalPrice totals={lineItem.totals} />
              </div>
              {isPriceDiscounted(lineItem) && (
                <div className="li-cell-summary--total-base-price">
                  <LineItemTotalBasePrice totals={lineItem.totals} />
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={className('trash-button-area', {
            'vd-ml2': reactSaleSummary,
            'vd-ml1 li-cell-summary--remove': !reactSaleSummary,
          })}
        >
          {locked ? (
            <LineItemLockedIcon lineItemID={lineItem.localId} />
          ) : (
            <button
              onClick={onRemove}
              className={'vd-btn vd-btn--icon-no'}
              type="button"
              aria-label="Delete product"
            >
              <Icon icon="trash" />
            </button>
          )}
        </div>
      </li>
    </ul>
  )
}

export const QuantitySummaryText: React.FC<{
  quantity: string | number
  prefix?: string
}> = ({ quantity, prefix = '' }) => (
  <div
    className={className('', {
      'li-cell-summary--quantity-negative': vn(quantity).lessThan(0),
    })}
  >
    <span className="li-cell-summary--quantity-text">
      {prefix}
      {displayRound(quantity, 3, true)}
    </span>
  </div>
)

export const LineItemDisplayComponent = reactular(
  LineItemDisplay,
  [
    'hasInvalidInput',
    'lineItem',
    'editAllowedForConfirmed',
    'onRemove',
    'onToggle',
  ],
  'reactWrapper'
)
